import React, { useEffect, useMemo, useState } from "react";
import "./PWAInstallPrompt.css";

const DISMISS_STORAGE_KEY = "elsa-pwa-install-dismissed-at";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function getPlatformInfo() {
  const userAgent = (navigator.userAgent || "").toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(userAgent);
  const isSafari =
    /safari/.test(userAgent) &&
    !/chrome|crios|fxios|edgios|opr|opera|android/.test(userAgent);

  return {
    isIOS,
    isAndroid,
    isIOSSafari: isIOS && isSafari,
    isIOSOtherBrowser: isIOS && !isSafari,
    isMobile: isIOS || isAndroid,
  };
}

function isStandaloneMode() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function readDismissedState() {
  let rawValue = null;

  try {
    rawValue = window.localStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return false;
  }

  const dismissedAt = Number(rawValue);

  if (!Number.isFinite(dismissedAt)) {
    return false;
  }

  return Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

const IOS_INSTALL_STEPS = [
  "Touchez Partager dans Safari.",
  "Choisissez Ajouter a l'ecran d'accueil.",
  "Activez Ouvrir comme app, puis validez avec Ajouter.",
];

const IOS_BROWSER_SWITCH_STEPS = [
  "Ouvrez cette page dans Safari.",
  "Dans Safari, touchez Partager.",
  "Choisissez Ajouter a l'ecran d'accueil, puis Ouvrir comme app.",
];

const ANDROID_INSTALL_STEPS = [
  "Ouvrez le menu du navigateur.",
  "Touchez Installer l'application ou Ajouter a l'ecran d'accueil.",
  "Confirmez l'installation.",
];

const PWAInstallPrompt = () => {
  const platform = useMemo(() => getPlatformInfo(), []);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneMode());
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneMode());
  const [isDismissed, setIsDismissed] = useState(() => readDismissedState());
  const [showAndroidFallback, setShowAndroidFallback] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const syncStandaloneState = () => {
      const standalone = isStandaloneMode();
      setIsStandalone(standalone);
      setIsInstalled(standalone);
    };

    syncStandaloneState();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncStandaloneState);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(syncStandaloneState);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", syncStandaloneState);
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(syncStandaloneState);
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowAndroidFallback(false);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
      setIsStandalone(true);
      setIsDismissed(false);
      try {
        window.localStorage.removeItem(DISMISS_STORAGE_KEY);
      } catch {
        // Ignore storage cleanup failures.
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (!platform.isAndroid || isStandalone || isInstalled || deferredPrompt) {
      setShowAndroidFallback(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setShowAndroidFallback(true);
    }, 6000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [deferredPrompt, isInstalled, isStandalone, platform.isAndroid]);

  const dismissPrompt = () => {
    setIsDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
    } catch {
      // Ignore storage failures and only dismiss for the current render.
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();

    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
    }
  };

  if (!platform.isMobile || isStandalone || isInstalled || isDismissed) {
    return null;
  }

  const showNativeInstall = Boolean(deferredPrompt);
  const showIOSInstructions = platform.isIOSSafari;
  const showIOSBrowserSwitch = platform.isIOSOtherBrowser;

  if (
    !showNativeInstall &&
    !showIOSInstructions &&
    !showIOSBrowserSwitch &&
    !showAndroidFallback
  ) {
    return null;
  }

  let title = "Installer ELSA GESTION";
  let description =
    "Ajoutez l'application a votre ecran d'accueil pour un acces plus rapide et une experience plus proche d'une application native.";
  let steps = [];
  let actionLabel = null;
  let secondaryNote = null;

  if (showNativeInstall) {
    actionLabel = "Installer maintenant";
    secondaryNote =
      "Android permet ici d'ouvrir directement la boite d'installation du navigateur.";
  } else if (showIOSInstructions) {
    title = "Ajouter l'application sur iPhone";
    description =
      "Sur iPhone, Safari ne montre pas de fenetre d'installation automatique. L'ajout se fait manuellement depuis le menu de partage.";
    steps = IOS_INSTALL_STEPS;
    secondaryNote =
      "Une fois ajoutee, l'application s'ouvrira comme une app sur l'ecran d'accueil.";
  } else if (showIOSBrowserSwitch) {
    title = "Installation iPhone via Safari";
    description =
      "Sur iPhone, l'installation PWA complete se fait avec Safari. Depuis ce navigateur, ouvrez la page dans Safari puis ajoutez-la a l'ecran d'accueil.";
    steps = IOS_BROWSER_SWITCH_STEPS;
  } else if (showAndroidFallback) {
    title = "Installer l'application sur Android";
    description =
      "Si Android ne montre pas encore de popup, vous pouvez lancer l'installation depuis le menu du navigateur.";
    steps = ANDROID_INSTALL_STEPS;
    secondaryNote =
      "Selon le navigateur, l'intitule peut varier entre Installer l'application et Ajouter a l'ecran d'accueil.";
  }

  return (
    <aside className="pwa-install-prompt" aria-live="polite">
      <button
        type="button"
        className="pwa-install-prompt__close"
        onClick={dismissPrompt}
        aria-label="Fermer l'aide d'installation"
      >
        x
      </button>

      <div className="pwa-install-prompt__eyebrow">Application mobile</div>
      <h2 className="pwa-install-prompt__title">{title}</h2>
      <p className="pwa-install-prompt__description">{description}</p>

      {steps.length > 0 && (
        <ol className="pwa-install-prompt__steps">
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}

      <div className="pwa-install-prompt__actions">
        {actionLabel && (
          <button
            type="button"
            className="pwa-install-prompt__primary"
            onClick={handleInstall}
          >
            {actionLabel}
          </button>
        )}
        <button
          type="button"
          className="pwa-install-prompt__secondary"
          onClick={dismissPrompt}
        >
          Plus tard
        </button>
      </div>

      {secondaryNote && (
        <p className="pwa-install-prompt__note">{secondaryNote}</p>
      )}
    </aside>
  );
};

export default PWAInstallPrompt;
